import { z } from 'zod';
import {
  paginatedSchema,
  placeSupplyResultSchema,
  supplyPreviewSchema,
  supplyReportSchema,
  supplyReportWithLinesSchema,
  type Paginated,
  type PlaceSupplyInput,
  type SupplyPreviewDto,
  type SupplyReportDto,
  type SupplyReportStatus,
  type SupplyReportWithLinesDto,
} from '@eombor/contracts';
import { apiClient } from '../../auth/model/auth.store.js';
import type { ApiClient } from '../../../shared/api/client.js';

/**
 * 1C moddiy hisoboti endpointlari. Manba:
 *   `stock/infrastructure/http/supply.controller.ts`
 *
 * Fayl `FormData` bilan ketadi va `Content-Type` QO'LDA qo'yilmaydi —
 * klient buni o'zi hal qiladi (`shared/api/client.ts`): qo'lda yozilgan
 * sarlavha `multipart` chegara belgisini yo'qotib, so'rovni buzardi.
 */

const reportPageSchema = paginatedSchema(supplyReportSchema);

/**
 * Natija shartnomadan olinadi — qo'lda takrorlangan shakl bir kun jimgina
 * ajralib qolardi. `docNos` KO'PLIK: joylashtirilganlar ta'minotchi
 * bo'yicha guruhlanadi va har guruhga o'z kirim hujjati yoziladi.
 */
const placeResultSchema = placeSupplyResultSchema;

export type PlaceResultDto = z.infer<typeof placeResultSchema>;

const createResultSchema = z.object({
  id: z.string(),
  docNo: z.string(),
  lineCount: z.number(),
  newCodeCount: z.number(),
});

export type CreateSupplyResultDto = z.infer<typeof createResultSchema>;

export interface SupplyQuery {
  warehouseId?: string;
  status?: SupplyReportStatus;
  page?: number;
  limit?: number;
}

export interface SupplyApi {
  /** Bazaga yozmasdan faylni o'qib ko'rsatadi. */
  preview: (file: File) => Promise<SupplyPreviewDto>;
  create: (warehouseId: string, file: File, note?: string) => Promise<CreateSupplyResultDto>;
  list: (query?: SupplyQuery) => Promise<Paginated<SupplyReportDto>>;
  getById: (id: string) => Promise<SupplyReportWithLinesDto>;
  /** Qoralamani skladchiga yuboradi — shu daqiqada xabar ketadi. */
  send: (id: string) => Promise<void>;
  /** Qoralamani butunlay o'chiradi. Yuborilgani o'chirilmaydi (409). */
  remove: (id: string) => Promise<void>;
  place: (id: string, body: PlaceSupplyInput) => Promise<PlaceResultDto>;
}

const queryString = (query: SupplyQuery): string => {
  const params = new URLSearchParams({
    page: String(query.page ?? 1),
    limit: String(query.limit ?? 20),
  });
  if (query.warehouseId !== undefined && query.warehouseId !== '') {
    params.set('warehouseId', query.warehouseId);
  }
  if (query.status !== undefined) params.set('status', query.status);
  return params.toString();
};

export const createSupplyApi = (client: ApiClient): SupplyApi => ({
  preview: (file) => {
    const form = new FormData();
    form.append('file', file);
    return client.apiFetch('/supply-reports/preview', {
      method: 'POST',
      body: form,
      schema: supplyPreviewSchema,
    });
  },

  create: (warehouseId, file, note) => {
    const form = new FormData();
    form.append('warehouseId', warehouseId);
    if (note !== undefined && note !== '') form.append('note', note);
    form.append('file', file);
    return client.apiFetch('/supply-reports', {
      method: 'POST',
      body: form,
      schema: createResultSchema,
    });
  },

  list: (query = {}) =>
    client.apiFetch(`/supply-reports?${queryString(query)}`, { schema: reportPageSchema }),

  getById: (id) =>
    client.apiFetch(`/supply-reports/${encodeURIComponent(id)}`, {
      schema: supplyReportWithLinesSchema,
    }),

  send: (id) =>
    client.apiFetch(`/supply-reports/${encodeURIComponent(id)}/send`, { method: 'POST' }),

  remove: (id) =>
    client.apiFetch(`/supply-reports/${encodeURIComponent(id)}`, { method: 'DELETE' }),

  place: (id, body) =>
    client.apiFetch(`/supply-reports/${encodeURIComponent(id)}/place`, {
      method: 'POST',
      body: JSON.stringify(body),
      schema: placeResultSchema,
    }),
});

export const supplyApi = createSupplyApi(apiClient);
